export const getOptimizedImageUrl = (image, options = {}) => {
    const baseUrl = image?.fields?.file?.url;
    if (!baseUrl) return '';

    const url = baseUrl.startsWith('http') ? baseUrl : `https:${baseUrl}`;

    const params = new URLSearchParams();

    // Determine if we're on mobile based on screen width
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const config = isMobile ? (options.mobile || {}) : (options.desktop || {});

    if (config.width) params.append('w', config.width);
    if (config.height) params.append('h', config.height);
    if (config.quality) params.append('q', config.quality);
    if (config.fit) params.append('fit', config.fit);
    params.append('fm', 'webp'); // Use WebP format for better compression

    return `${url}?${params.toString()}`;
};

export const getThumbnailUrl = (image, options = {}) => {
    // Get original dimensions to determine orientation
    const originalWidth = image?.fields?.file?.details?.image?.width;
    const originalHeight = image?.fields?.file?.details?.image?.height;

    let thumbnailConfig;

    if (originalWidth && originalHeight) {
        const isPortrait = originalHeight > originalWidth;
        const isLandscape = originalWidth > originalHeight;

        if (isPortrait) {
            // For portraits: constrain by height
            thumbnailConfig = {
                mobile: {
                    height: 400,
                    quality: 95,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 95,
                    fit: 'fill'
                }
            };
        } else if (isLandscape) {
            // For landscapes: constrain by width
            thumbnailConfig = {
                mobile: {
                    width: 200,
                    quality: 95,
                    fit: 'fill'
                },
                desktop: {
                    width: 400,
                    quality: 95,
                    fit: 'fill'
                }
            };
        } else {
            // For square images: use height constraint
            thumbnailConfig = {
                mobile: {
                    height: 200,
                    quality: 95,
                    fit: 'fill'
                },
                desktop: {
                    height: 400,
                    quality: 95,
                    fit: 'fill'
                }
            };
        }
    } else {
        // Fallback to original config
        thumbnailConfig = {
            mobile: {
                height: 200,
                quality: 85,
                fit: 'fill'
            },
            desktop: {
                height: 400,
                quality: 85,
                fit: 'fill'
            }
        };
    }

    return getOptimizedImageUrl(image, { ...thumbnailConfig, ...options });
};

// Returns a DPR-aware optimized URL for either a Contentful asset object or a direct URL string.
export const getDprImageUrl = (source, baseMax = 600, opts = {}) => {
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const maxPx = Math.ceil(baseMax * Math.min(dpr, 3));

    // If it's a Contentful asset object with dimensions, pick width/height based on orientation
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) return '';

    const url = fileUrl.startsWith('http') ? fileUrl : `https:${fileUrl}`;

    const params = new URLSearchParams();
    const originalWidth = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width;
    const originalHeight = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height;

    if (originalWidth && originalHeight) {
        if (originalWidth >= originalHeight) {
            params.set('w', String(Math.min(originalWidth, maxPx)));
        } else {
            params.set('h', String(Math.min(originalHeight, maxPx)));
        }
    } else {
        // Fallback when dimensions are unknown: constrain by width
        params.set('w', String(maxPx));
    }

    params.set('fm', opts.format || 'webp');
    params.set('q', String(opts.quality || 85));
    if (opts.fit) params.set('fit', opts.fit);

    // Preserve existing query parameters if any
    const hasQuery = url.includes('?');
    if (hasQuery) {
        const [base, existing] = url.split('?');
        const existingParams = new URLSearchParams(existing);
        // Do not overwrite explicit existing sizing params
        for (const [k, v] of existingParams.entries()) {
            if (!params.has(k)) params.set(k, v);
        }
        return `${base}?${params.toString()}`;
    }
    return `${url}?${params.toString()}`;
};

/**
 * Get medium-sized optimized image URL from Contentful for desktop
 * 600px as the longest side, multiplied by device pixel density
 * Uses 85% compression quality
 */
export function getMediumImageUrl(source, opts = {}) {
    // Get device pixel ratio (capped at 3 for performance)
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const baseLongestSide = 750;
    const maxPx = Math.ceil(baseLongestSide * Math.min(dpr, 3));

    // Extract file URL from Contentful asset object or use direct URL string
    const fileUrl = typeof source === 'string'
        ? source
        : source?.fields?.file?.url;

    if (!fileUrl) return '';

    const url = fileUrl.startsWith('http') ? fileUrl : `https:${fileUrl}`;

    // Get original dimensions from Contentful asset
    const originalWidth = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.width;
    const originalHeight = typeof source === 'string' ? undefined : source?.fields?.file?.details?.image?.height;

    const params = new URLSearchParams();

    if (originalWidth && originalHeight) {
        // Determine orientation and set appropriate dimension constraint
        if (originalHeight > originalWidth) {
            // Portrait: height should be 600 * DPR
            params.set('h', String(maxPx));
        } else {
            // Landscape or square: width should be 600 * DPR
            params.set('w', String(maxPx));
        }
    } else {
        // Fallback when dimensions are unknown: constrain by width
        params.set('w', String(maxPx));
    }

    // Set format and quality
    params.set('fm', opts.format || 'jpg');
    params.set('q', String(opts.quality || 85));

    // Optional fit parameter
    if (opts.fit) params.set('fit', opts.fit);

    return `${url}?${params.toString()}`;
}

/**
 * Get small-sized optimized image URL from Contentful
 * 400px as the base size, multiplied by device pixel density
 * Uses JPG format by default with 85% compression quality
 */
export function getSmallImageUrl(source, opts = {}) {
    return getDprImageUrl(source, 400, { format: 'jpg', ...opts });
}
